/**
 * Autenticação via API (Turso).
 * Password enviada em plain-text via HTTPS — bcrypt feito no servidor.
 * JWT token guardado em localStorage junto com id/name/email.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "@/lib/apiClient";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role?: string;
}

interface AuthState {
  user:          AuthUser | null;
  token:         string | null;
  emailVerified: boolean;

  register:        (name: string, email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  login:           (email: string, password: string)               => Promise<{ ok: boolean; error?: string }>;
  masterLogin:     (email: string, password: string)               => Promise<{ ok: boolean; error?: string }>;
  setFromOAuth:    (user: AuthUser, token: string)                 => void;
  setEmailVerified: () => void;
  logout:          () => void;
  isAuthenticated: () => boolean;
}

function genId(): string {
  return `user_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user:          null,
      token:         null,
      emailVerified: false,

      register: async (name, email, password) => {
        if (password.length < 6)
          return { ok: false, error: "A password deve ter pelo menos 6 caracteres." };

        const id = genId();

        try {
          const result = await api.auth.register({ id, name, email, password });
          set({ user: result.user, token: result.token, emailVerified: false });
          return { ok: true, emailSent: result.emailSent };
        } catch (err: unknown) {
          const text = err instanceof Error ? err.message : "";
          const msg = text.includes("409")
            ? "Este e-mail já está em uso."
            : text.includes("400")
            ? "Dados inválidos. Verifica o e-mail e a password."
            : "Erro ao criar conta. Tenta novamente.";
          return { ok: false, error: msg };
        }
      },

      login: async (email, password) => {
        try {
          const result = await api.auth.login({ email, password });
          set({ user: result.user, token: result.token, emailVerified: !!result.emailVerified });
          return { ok: true, emailVerified: !!result.emailVerified } as any;
        } catch (err: unknown) {
          const text = err instanceof Error ? err.message : "";
          let msg = "Erro ao iniciar sessão. Tenta novamente.";
          if (text.includes("401")) msg = "E-mail ou password incorrectos.";
          else if (text.includes("422")) msg = "Dados inválidos. Verifica o e-mail e a password.";
          else if (text.includes("429")) msg = "Demasiadas tentativas. Aguarda alguns minutos.";
          else if (text.includes("Failed to fetch") || text.includes("NetworkError")) msg = "Sem ligação ao servidor. Verifica a tua internet.";
          return { ok: false, error: msg };
        }
      },

      masterLogin: async (email, password) => {
        try {
          const res = await fetch("/api/auth/master/login", {
            method:  "POST",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({ email, password }),
          });
          const data = await res.json();
          if (!res.ok) {
            return { ok: false, error: data.message ?? "Credenciais inválidas." };
          }
          set({ user: data.user, token: data.token, emailVerified: true });
          return { ok: true };
        } catch {
          return { ok: false, error: "Sem ligação ao servidor. Verifica a tua internet." };
        }
      },

      setFromOAuth: (user, token) => set({ user, token, emailVerified: true }),

      setEmailVerified: () => set({ emailVerified: true }),

      logout: () => {
        api.auth.logout().catch(() => {});
        set({ user: null, token: null, emailVerified: false });
      },

      isAuthenticated: () => get().user !== null && get().token !== null,
    }),
    { name: "trade-academy-auth" },
  ),
);
